import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { CheckCircle2, Lock, Radar, RefreshCw, ShieldCheck } from 'lucide-react';
import { useDemo } from '../context/DemoContext';
import { C, dt, errMsg, http, img, num, R, Rm, tip } from '../lib/api';
import { PageHeader, Pill } from '../components/common';
import { ProvinceMiniMap } from '../components/geo/geo';

const PERSONAS = [
  { id: 'USR-FIN', key: 'lender', title: 'Lender View', focus: 'Portfolio, Risk & Repayments', accent: '#FFCD00' },
  { id: 'USR-SME', key: 'sme', title: 'SME Operator View', focus: 'Assets, Operations & Support', accent: '#22D3EE' },
  { id: 'USR-ADV', key: 'equify', title: 'Barloworld / Equify View', focus: 'Clients, Equipment & Service', accent: '#10B981' },
  { id: 'USR-ADM', key: 'admin', title: 'Nsync Admin View', focus: 'Configuration & System Operation', accent: '#A78BFA' },
];
const CAPS = [
  ['app.create', 'Submit finance applications'], ['app.assess', 'Assess readiness & request evidence'], ['app.decide', 'Record simulated finance decisions'],
  ['equipment.update', 'Update equipment status & downtime'], ['receipt.update', 'Record delayed customer receipts'], ['instalment.flag', 'Flag finance instalments'],
  ['case.manage', 'Assign & resolve intervention cases'], ['insights.view', 'Portfolio insights & analytics'], ['admin.manage', 'Configure demo, users & roles'], ['demo.reset', 'Reset demonstration data'],
];
const PROBES = [
  ['Application APP-1001 (Ubuntu, Meridian lender)', '/applications/APP-1001'], ['Equipment EQ-003 (Maseko, Highveld lender)', '/equipment/EQ-003'],
  ['Finance agreements register', '/finance/agreements'], ['Case CASE-102 (finance review)', '/cases/CASE-102'], ['Case CASE-101 (equipment service)', '/cases/CASE-101'],
  ['SME profile SME-004 (Mokopane)', '/smes/SME-004'], ['Demo configuration & users', '/admin/overview'],
];
const PIE = [C.yellow, C.teal, C.blue, C.green, C.orange, C.cyan, '#A78BFA'];

function useAs(path, uid) {
  const { version } = useDemo();
  const [s, setS] = useState({ loading: true });
  useEffect(() => {
    let on = true;
    http.get(path, { headers: { 'X-Demo-User': uid } })
      .then((r) => on && setS({ data: r.data }))
      .catch((e) => on && setS({ error: errMsg(e), status: e?.response?.status }));
    return () => { on = false; };
  }, [path, uid, version]);
  return s;
}

const Mini = ({ label, value, tone = 'text-white' }) => (
  <div className="rounded border border-[#1E2A45] bg-[#0D1424] px-2.5 py-2"><div className="eyebrow text-[9px]">{label}</div><div className={`mt-0.5 font-mono text-base font-bold ${tone}`}>{value}</div></div>
);
const Sub = ({ children }) => <div className="eyebrow mb-1.5 mt-4">{children}</div>;
const Row = ({ a, b, c, to }) => {
  const inner = <><span className="min-w-0 flex-1 truncate">{a}</span>{b && <span className="font-mono text-[11px] text-slate-400">{b}</span>}{c}</>;
  return to ? <Link to={to} className="flex items-center gap-2 border-b border-[#1E2A45]/70 py-1.5 text-xs last:border-0 hover:text-[#FFCD00]">{inner}</Link>
    : <div className="flex items-center gap-2 border-b border-[#1E2A45]/70 py-1.5 text-xs last:border-0">{inner}</div>;
};

function LenderBody({ d }) {
  const k = d.kpis;
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Mini label="Portfolio value" value={Rm(k.exposure)} tone="text-[#FFCD00]" /><Mini label="Active clients" value={k.active_smes} />
        <Mini label="Total assets" value={k.financed_assets} /><Mini label="At-risk exposure" value={Rm(k.at_risk_exposure)} tone="text-red-300" />
      </div>
      <Sub>Portfolio trend · revenue · collections · repayments</Sub>
      <div className="h-28"><ResponsiveContainer><LineChart data={d.trend}><XAxis dataKey="label" stroke={C.muted} fontSize={9} /><Tooltip {...tip} formatter={(v) => Rm(v)} />
        <Line dataKey="billed" name="Revenue" stroke={C.teal} dot={false} /><Line dataKey="collected" name="Collections" stroke={C.yellow} dot={false} /><Line dataKey="due" name="Repayments" stroke={C.red} dot={false} strokeDasharray="3 3" /></LineChart></ResponsiveContainer></div>
      <div className="mt-3 grid grid-cols-5 gap-2">
        <div className="col-span-3"><Sub>Geographic exposure</Sub><ProvinceMiniMap stats={Object.fromEntries(d.geo.map((g) => [g.code, g]))} height={150} legend={false} testid="lender-geo-map" /></div>
        <div className="col-span-2"><Sub>By sector</Sub><div className="h-[150px]"><ResponsiveContainer><PieChart><Pie data={d.sectors} dataKey="exposure" nameKey="sector" innerRadius={28} outerRadius={52} stroke="none">
          {d.sectors.map((s, i) => <Cell key={s.sector} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip {...tip} formatter={(v) => Rm(v)} /></PieChart></ResponsiveContainer></div></div>
      </div>
      <Sub>Upcoming actions · instalments</Sub>
      {d.upcoming.slice(0, 4).map((r) => <Row key={r.id} a={`${r.agreement_id} #${r.number}`} b={dt(r.due_date)} c={<Pill v={r.status} />} />)}
      <Sub>Recent applications</Sub>
      {d.applications.slice(0, 4).map((a) => <Row key={a.id} to={`/applications/${a.id}`} a={`${d.sme_names[a.sme_id]} · CAT ${a.equipment.model}`} b={Rm(a.amount)} c={<Pill v={a.status} />} />)}
      <Sub>Alerts & exceptions</Sub>
      {d.exceptions.length ? d.exceptions.slice(0, 3).map((x) => <Row key={x.id} a={x.title} c={<Pill v={x.severity} />} />) : <div className="text-xs text-slate-500">No open finance exceptions.</div>}
    </>
  );
}

function SmeBody({ d }) {
  return (
    <>
      <div className="relative overflow-hidden rounded-md border border-[#1E2A45]">
        <img src={img('exc1')} alt="Excavator — illustrative" className="h-24 w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1D] to-transparent p-3"><div className="font-display font-semibold text-white">{d.sme.name}</div><div className="text-[11px] text-slate-300">{d.sme.sector} · {d.sme.province_name}</div></div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2"><Mini label="Finance balance" value={Rm(d.kpis.exposure)} tone="text-[#FFCD00]" /><Mini label="Monthly instalments" value={Rm(d.kpis.monthly_instalments)} /></div>
      <Sub>Owned / financed equipment</Sub>
      <div className="grid grid-cols-2 gap-2">{d.equipment.slice(0, 4).map((e) => (
        <Link key={e.id} to={`/equipment/${e.id}`} className="overflow-hidden rounded border border-[#1E2A45] hover:border-[#22D3EE]/60"><img src={img(e.image)} alt={e.category} className="h-14 w-full object-cover" />
          <div className="flex items-center justify-between px-1.5 py-1 text-[10px]"><span className="truncate">{e.id} · {num(e.hours_meter)}h</span><Pill v={e.status} /></div></Link>))}</div>
      <Sub>Repayment schedule</Sub>
      {d.agreements.slice(0, 3).map((a) => <Row key={a.id} a={a.id} b={`${R(a.instalment)}/m`} c={<span className="text-[10px] text-slate-500">{a.next_due ? dt(a.next_due.due_date) : '—'}</span>} />)}
      <Sub>Evidence requests & tasks</Sub>
      {[...d.evidence_requests.map((e) => ({ id: e.id, t: e.label, to: `/applications/${e.application_id}?tab=documents` })), ...d.tasks.slice(0, 3).map((t) => ({ id: t.id, t: t.title }))].slice(0, 4).map((x) => <Row key={x.id} a={x.t} to={x.to} />)}
      {d.evidence_requests.length + d.tasks.length === 0 && <div className="text-xs text-slate-500">Nothing outstanding.</div>}
      <Sub>Support & service requests</Sub>
      {d.cases.length ? d.cases.slice(0, 3).map((c) => <Row key={c.id} to={`/interventions/${c.id}`} a={c.title} c={<Pill v={c.status} />} />) : <div className="text-xs text-slate-500">No open cases.</div>}
    </>
  );
}

function EquifyBody({ d }) {
  const k = d.kpis;
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Mini label="Client portfolio" value={k.total_smes} /><Mini label="Equipment" value={k.financed_assets + k.pending_assets} />
        <Mini label="Open interventions" value={k.open_interventions} tone="text-orange-300" /><Mini label="Live exceptions" value={k.open_exceptions} tone="text-red-300" />
      </div>
      <Sub>Equipment health & utilisation</Sub>
      <div className="flex flex-wrap gap-1.5">{Object.entries(k.equipment_status).map(([s, n]) => <span key={s} className="flex items-center gap-1"><Pill v={s} /><span className="font-mono text-[11px]">{n}</span></span>)}</div>
      <Sub>Client health</Sub>
      {d.health.slice(0, 6).map((h) => <Row key={h.sme_id} to={`/smes/${h.sme_id}`} a={h.name} c={<Pill v={h.status} />} />)}
      <Sub>Upcoming actions · cases & service</Sub>
      {d.cases.slice(0, 4).map((c) => <Row key={c.id} to={`/interventions/${c.id}`} a={`${c.id} · ${c.title}`} c={<Pill v={c.priority} />} />)}
      <Sub>Exceptions</Sub>
      {d.exceptions.slice(0, 3).map((x) => <Row key={x.id} a={x.title} c={<Pill v={x.category} />} />)}
    </>
  );
}

function AdminBody({ d }) {
  const o = d.overview;
  return (
    <>
      <div className="grid grid-cols-2 gap-2"><Mini label="API" value={o.health.api} tone="text-emerald-300" /><Mini label="Database" value={o.health.database} tone="text-emerald-300" />
        <Mini label="Users" value={o.counts.users} /><Mini label="Audit events" value={o.counts.audit} /></div>
      <Sub>Synthetic data quality · record counts</Sub>
      {Object.entries(o.counts).filter(([kk]) => !['users', 'audit'].includes(kk)).map(([kk, v]) => <Row key={kk} a={kk} b={num(v)} />)}
      <Sub>Workflow configuration</Sub>
      <Row a="Reporting period" b={o.config.reporting_period} /><Row a="Presenter" b={o.config.presenter} />
      <div className="mt-3 flex items-start gap-2 rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10 p-2 text-[11px] text-violet-200"><Lock className="h-3.5 w-3.5 shrink-0" />No access to confidential SME or finance data — enforced by backend (HTTP 403).</div>
      <Link to="/admin" className="mt-2 inline-block text-xs text-[#A78BFA]">Administration & audit →</Link>
    </>
  );
}

const BODY = { lender: LenderBody, sme: SmeBody, equify: EquifyBody, admin: AdminBody };

function PersonaPanel({ p, probe }) {
  const ws = useAs('/workspace', p.id);
  const me = useAs('/me', p.id);
  const { switchUser } = useDemo();
  const nav = useNavigate();
  const Body = BODY[p.key];
  const perms = new Set(me.data?.permissions || []);
  return (
    <section className="panel fade-up flex flex-col overflow-hidden" style={{ borderTopColor: p.accent, borderTopWidth: 3 }} data-testid={`stakeholder-panel-${p.key}`}>
      <div className="border-b border-[#1E2A45] px-4 py-3">
        <div className="flex items-center justify-between gap-2"><h3 className="font-display text-base font-bold text-white">{p.title}</h3>
          {probe && <span data-testid={`probe-result-${p.key}`} className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${probe.ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>{probe.ok ? 'ALLOWED' : probe.code === 404 ? 'NOT VISIBLE' : 'BLOCKED'} · {probe.code}</span>}</div>
        <div className="text-[11px] text-slate-400">{me.data ? `${me.data.name} · ${me.data.org_name}` : '…'}</div>
        <div className="mt-1 text-[11px]" style={{ color: p.accent }}>Focus: {p.focus}</div>
      </div>
      <div className="flex-1 px-4 py-3">{ws.error ? <div className="text-xs text-red-300">{ws.error}</div> : ws.data ? <Body d={ws.data} /> : <div className="text-xs text-slate-500">Loading under {p.id} permissions…</div>}</div>
      <div className="border-t border-[#1E2A45] px-4 py-3">
        <div className="eyebrow mb-1.5">Capabilities for this role</div>
        <div className="space-y-1">{CAPS.map(([c, l]) => (
          <div key={c} className={`flex items-center gap-1.5 text-[11px] ${perms.has(c) ? 'text-slate-200' : 'text-slate-600'}`} data-testid={`cap-${p.key}-${c}`}>
            {perms.has(c) ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : <Lock className="h-3 w-3" />}{l}</div>))}</div>
        <button className="btn-outline mt-3 w-full py-1.5 text-xs" style={{ borderColor: `${p.accent}66` }} data-testid={`open-workspace-${p.key}`}
          onClick={() => { switchUser(p.id); nav(p.key === 'admin' ? '/workspace' : p.key === 'equify' ? '/overview' : '/workspace'); }}>Enter this workspace →</button>
      </div>
    </section>
  );
}

export default function Stakeholders() {
  const { bump } = useDemo();
  const [probePath, setProbePath] = useState(PROBES[0][1]);
  const [probe, setProbe] = useState({});
  const [busy, setBusy] = useState(false);
  const runProbe = async () => {
    setBusy(true);
    const res = await Promise.all(PERSONAS.map((p) => http.get(probePath, { headers: { 'X-Demo-User': p.id } }).then(() => [p.key, { ok: true, code: 200 }]).catch((e) => [p.key, { ok: false, code: e?.response?.status || 0 }])));
    setProbe(Object.fromEntries(res));
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="stakeholders-page">
      <PageHeader code="W08" title="Stakeholder Workspaces — side by side" crumbs={[{ label: 'Workspaces' }, { label: 'Stakeholder comparison' }]}
        subtitle="Four live workspaces on the same shared dataset. Each panel is fetched under that persona's own backend permissions — right view, right role."
        actions={<button className="btn-outline" onClick={bump} data-testid="stakeholders-refresh-btn"><RefreshCw className="h-4 w-4" />Refresh all views</button>} />
      <div className="panel flex flex-wrap items-center gap-3 p-4 fade-up" data-testid="access-probe">
        <Radar className="h-5 w-5 text-[#FFCD00]" />
        <div className="mr-2"><div className="font-display font-semibold text-white">Permission probe</div><div className="text-[11px] text-slate-400">Request the same record as all four roles and compare the backend response.</div></div>
        <select className="input max-w-sm" value={probePath} onChange={(e) => { setProbePath(e.target.value); setProbe({}); }} data-testid="probe-select">{PROBES.map(([l, v]) => <option key={v} value={v}>{l}</option>)}</select>
        <button className="btn-primary" onClick={runProbe} disabled={busy} data-testid="probe-run-btn"><ShieldCheck className="h-4 w-4" />{busy ? 'Testing…' : 'Test access across roles'}</button>
        {Object.keys(probe).length > 0 && <div className="flex flex-wrap gap-2 text-[11px]" data-testid="probe-summary">{PERSONAS.map((p) => <span key={p.key} style={{ color: p.accent }}>{p.title.replace(' View', '')}: <b className={probe[p.key]?.ok ? 'text-emerald-300' : 'text-red-300'}>{probe[p.key]?.ok ? 'allowed' : 'blocked'}</b></span>)}</div>}
      </div>
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">{PERSONAS.map((p) => <PersonaPanel key={p.id} p={p} probe={probe[p.key]} />)}</div>
      <section className="panel grid gap-4 p-5 lg:grid-cols-[1fr_2fr] fade-up" data-testid="right-view-right-role">
        <div><div className="font-mono text-[10px] tracking-[0.2em] text-[#FFCD00]">RIGHT VIEW, RIGHT ROLE</div>
          <p className="mt-2 text-sm text-slate-300">Lender, SME operator, Barloworld/Equify and Nsync administration views share one connected record set, while granular permissions and organisation-level isolation keep confidential data with the people entitled to see it.</p>
          <p className="mt-2 text-[11px] text-slate-500">Presenter comparison uses the demonstration personas from the role switcher. Data is synthetic.</p></div>
        <div className="overflow-x-auto"><table className="tbl"><thead><tr><th>Capability</th>{PERSONAS.map((p) => <th key={p.key} style={{ color: p.accent }}>{p.title.replace(' View', '')}</th>)}</tr></thead>
          <tbody><MatrixRows /></tbody></table></div>
      </section>
    </div>
  );
}

function MatrixRows() {
  const a = useAs('/me', PERSONAS[0].id);
  const b = useAs('/me', PERSONAS[1].id);
  const c2 = useAs('/me', PERSONAS[2].id);
  const d = useAs('/me', PERSONAS[3].id);
  const all = [a, b, c2, d];
  return CAPS.map(([c, l]) => (
    <tr key={c}><td className="text-xs">{l}</td>{all.map((m, i) => <td key={PERSONAS[i].key}>{(m.data?.permissions || []).includes(c) ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Lock className="h-3.5 w-3.5 text-slate-600" />}</td>)}</tr>
  ));
}
